import { Component, Host, h, Prop, Element } from '@stencil/core';

// Round to smallest `roundToMin` number.
// ex. minTo(38, 5) = 35
//     minTo(33, 5) = 30
function minTo(number: number, roundtoMin: number) {
  return roundtoMin * Math.floor(number / roundtoMin);
}

export interface AhaProgressBar extends HTMLAhaProgressBarElement {}

/**
 * @slot DEFAULT - Contents of label
 */
@Component({
  tag: 'aha-progress-bar',
  styleUrl: 'aha-progress-bar.css',
  shadow: true,
})
export class AhaProgressBar {
  @Element() host!: HTMLAhaProgressBarElement;

  /** Height of progress bar. */
  @Prop({ reflect: true }) height: string = '3em';

  /** Total number of records. */
  @Prop({ reflect: true }) total: number = 100;

  /** Number of records that are completed. */
  @Prop({ reflect: true }) completed: number = 0;

  /** Text that will say how completed units are measured (ex. "%", "records", "features", etc.). */
  @Prop({ reflect: true }) units!: string;

  /** Hide all textual labels. */
  @Prop({ reflect: true, attribute: 'no-text' }) noText: boolean = false;

  render() {
    const rawPercent = (100 * Math.min(this.completed, this.total)) / this.total;
    const percent = Math.round(rawPercent);
    const paddedUnits = this.units?.trim()?.length > 1 ? ` ${this.units}` : this.units;
    const title = this.noText
      ? ''
      : `${percent}${paddedUnits} ${this.host.textContent?.trim()}`.trim();

    // Will show a class rounded to the percent of the nearest 5, 10, and 25 divisor.
    // So 87% complete will show `percent-85`, `percent-80`, and `percent-75` as class names,
    // allowing you to style the part appropriately.
    const css = new Set();
    css.add(`percent-${minTo(percent, 5)}`);
    css.add(`percent-${minTo(percent, 10)}`);
    css.add(`percent-${minTo(percent, 25)}`);

    return (
      <Host class={Array.from(css).join(' ')} style={{ '--aha-progress-bar--height': this.height }}>
        <div id="bar" style={{ width: `${rawPercent}%` }} title={title}></div>
        <div id="text">
          {!this.noText && (
            <span id="counter">
              {percent}
              <span id="units">{paddedUnits}</span>
            </span>
          )}
          <slot></slot>
        </div>
      </Host>
    );
  }
}
